import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, BookOpen, Layers, Lock, ArrowRight, SearchX } from "lucide-react";
import api from "@/lib/api";
import { Overline, Loading } from "@/components/shared";

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setData(r.data));
  }, [q]);

  if (!data) return <Loading label="Searching…" />;

  return (
    <div className="max-w-3xl" data-testid="search-page">
      <Overline>Search</Overline>
      <h1 className="font-serif text-4xl text-nm-ink mt-2">Results for “{q}”</h1>
      <p className="text-nm-neutral mt-2">{data.total} result{data.total === 1 ? "" : "s"} across chapters, templates, and sections.</p>

      {data.total === 0 && (
        <div className="mt-12 text-center text-nm-neutral" data-testid="no-results">
          <SearchX size={40} className="mx-auto text-nm-primary/30" />
          <p className="mt-4">No matches. Try a broader term like “delegate”, “priorities”, or “capacity”.</p>
        </div>
      )}

      {data.chapters.length > 0 && (
        <Group title="Chapters" icon={BookOpen}>
          {data.chapters.map((c) => (
            <ResultRow key={c.slug} to={`/app/chapter/${c.slug}`} testid={`result-ch-${c.slug}`}
              label={c.section_label} title={c.title} sub={c.summary} locked={!c.unlocked} />
          ))}
        </Group>
      )}
      {data.templates.length > 0 && (
        <Group title="Templates" icon={FileText}>
          {data.templates.map((t) => (
            <ResultRow key={t.slug} to={`/app/template/${t.slug}`} testid={`result-tpl-${t.slug}`}
              label={t.category} title={t.title} sub={t.summary} locked={!t.unlocked} green />
          ))}
        </Group>
      )}
      {data.sections.length > 0 && (
        <Group title="Sections" icon={Layers}>
          {data.sections.map((s) => (
            <ResultRow key={s.slug} to={`/app/part/${s.part === "start" ? "part_1" : s.part}`} testid={`result-sec-${s.slug}`}
              label={s.part === "part_2" ? "Part 2" : "Part 1"} title={s.title} sub={s.summary} />
          ))}
        </Group>
      )}
    </div>
  );
}

function Group({ title, icon: Icon, children }) {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 text-nm-primary font-medium text-sm uppercase tracking-wide mb-4"><Icon size={16} /> {title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ResultRow({ to, testid, label, title, sub, locked, green }) {
  return (
    <Link to={to} data-testid={testid}
      className="group flex items-start gap-4 rounded-2xl border border-nm-primary/10 bg-nm-surface p-5 nm-shadow-sm hover:-translate-y-0.5 transition-transform duration-300">
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${green ? "text-nm-green" : "text-nm-coral"}`}>{label}</div>
        <div className="font-serif text-lg text-nm-ink mt-0.5">{title}</div>
        {sub && <p className="text-sm text-nm-neutral mt-1 leading-relaxed line-clamp-2">{sub}</p>}
      </div>
      {locked ? <Lock size={16} className="text-nm-neutral/50 shrink-0 mt-1" /> : <ArrowRight size={16} className="text-nm-primary/50 shrink-0 mt-1 group-hover:translate-x-1 transition-transform duration-200" />}
    </Link>
  );
}
